import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ScienceDebugger, DiagnosticStep } from './ScienceDebugger';

interface CapturedError {
  id: string;
  timestamp: string;
  type: 'error' | 'unhandledRejection' | 'react-error' | 'console-error';
  message: string;
  stack?: string;
  componentStack?: string;
  fileName?: string;
  lineNumber?: number;
  columnNumber?: number;
  url?: string;
  userAgent: string;
  href: string;
  count: number;
}

interface ErrorPattern {
  pattern: string;
  count: number;
  lastSeen: string;
  firstSeen: string;
  type: string;
}

export const ErrorScience: React.FC = () => {
  const [errors, setErrors] = useState<CapturedError[]>([]);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [errorPatterns, setErrorPatterns] = useState<ErrorPattern[]>([]);
  const originalConsoleError = useRef<typeof console.error>();
  const errorListeners = useRef<Set<(event: any) => void>>(new Set());

  const captureError = useCallback((error: any, type: string) => {
    const errorEvent: CapturedError = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      type: type as CapturedError['type'],
      message: error?.message || error?.reason || String(error),
      stack: error?.stack,
      componentStack: error?.componentStack,
      fileName: error?.fileName || error?.filename,
      lineNumber: error?.lineNumber || error?.lineno,
      columnNumber: error?.columnNumber || error?.colno,
      url: error?.url,
      userAgent: navigator.userAgent,
      href: window.location.href,
      count: 1,
    };

    setErrors(prev => {
      // Check for duplicate errors
      const existingIndex = prev.findIndex(e =>
        e.message === errorEvent.message &&
        e.fileName === errorEvent.fileName &&
        e.lineNumber === errorEvent.lineNumber
      );

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          count: updated[existingIndex].count + 1,
          timestamp: errorEvent.timestamp,
        };
        return updated;
      }

      return [errorEvent, ...prev].slice(0, 100); // Keep last 100 errors
    });

    // Update patterns
    const pattern = error?.message?.split(/\d+/)[0] || 'Unknown'; // Remove numbers to find patterns
    setErrorPatterns(prev => {
      const existingPattern = prev.find(p => p.pattern === pattern);
      if (existingPattern) {
        return prev.map(p =>
          p.pattern === pattern
            ? { ...p, count: p.count + 1, lastSeen: new Date().toISOString() }
            : p
        );
      }
      return [...prev, {
        pattern,
        count: 1,
        firstSeen: new Date().toISOString(),
        lastSeen: new Date().toISOString(),
        type,
      }];
    });
  }, []);

  const startMonitoring = useCallback(() => {
    if (isMonitoring) return;

    // Capture window errors
    const windowErrorHandler = (event: ErrorEvent) => {
      captureError(event, 'error');
    };

    // Capture unhandled promise rejections
    const unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
      captureError({
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stack: event.reason?.stack,
      }, 'unhandledRejection');
    };

    // Override console.error
    originalConsoleError.current = console.error;
    console.error = (...args) => {
      captureError({
        message: args.map(arg =>
          typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
        ).join(' '),
      }, 'console-error');

      if (originalConsoleError.current) {
        originalConsoleError.current.apply(console, args);
      }
    };

    window.addEventListener('error', windowErrorHandler);
    window.addEventListener('unhandledrejection', unhandledRejectionHandler);

    errorListeners.current.add(windowErrorHandler);
    errorListeners.current.add(unhandledRejectionHandler);

    setIsMonitoring(true);
  }, [isMonitoring, captureError]);

  const stopMonitoring = useCallback(() => {
    if (!isMonitoring) return;

    // Restore console.error
    if (originalConsoleError.current) {
      console.error = originalConsoleError.current;
    }

    // Remove listeners
    errorListeners.current.forEach(listener => {
      window.removeEventListener('error', listener as any);
      window.removeEventListener('unhandledrejection', listener as any);
    });
    errorListeners.current.clear();

    setIsMonitoring(false);
  }, [isMonitoring]);

  useEffect(() => {
    // Auto-start monitoring
    startMonitoring();

    return () => {
      stopMonitoring();
    };
  }, []);

  const errorDiagnosticSteps: DiagnosticStep[] = [
    {
      id: 'hydration-errors',
      label: 'Check for React hydration errors',
      category: 'React',
      critical: true,
      description: 'Detecting server/client mismatches',
      check: async () => {
        const hydrationErrors = errors.filter(e =>
          e.message.toLowerCase().includes('hydrat') ||
          e.message.includes('Text content does not match') ||
          e.message.includes('did not match')
        );

        return {
          passed: hydrationErrors.length === 0,
          details: {
            count: hydrationErrors.length,
            errors: hydrationErrors.slice(0, 3).map(e => ({
              message: e.message,
              location: `${e.fileName}:${e.lineNumber}`,
            })),
            commonCauses: [
              'Date/time rendering',
              'Math.random() usage',
              'localStorage access during SSR',
              'window/document access during SSR',
            ],
          },
          error: hydrationErrors.length > 0
            ? `Found ${hydrationErrors.length} hydration errors`
            : undefined,
        };
      },
    },
    {
      id: 'console-errors',
      label: 'Console error analysis',
      category: 'Console',
      description: 'Analyzing console.error calls',
      check: async () => {
        const consoleErrors = errors.filter(e => e.type === 'console-error');
        const uniqueMessages = new Set(consoleErrors.map(e => e.message));

        return {
          passed: consoleErrors.length === 0,
          details: {
            totalErrors: consoleErrors.length,
            uniqueErrors: uniqueMessages.size,
            topErrors: Array.from(uniqueMessages).slice(0, 5),
          },
          error: consoleErrors.length > 0
            ? `${consoleErrors.length} console errors detected`
            : undefined,
        };
      },
    },
    {
      id: 'uncaught-errors',
      label: 'Uncaught JavaScript errors',
      category: 'JavaScript',
      critical: true,
      description: 'Checking for uncaught exceptions',
      check: async () => {
        const uncaughtErrors = errors.filter(e => e.type === 'error');

        return {
          passed: uncaughtErrors.length === 0,
          details: {
            count: uncaughtErrors.length,
            errors: uncaughtErrors.slice(0, 3).map(e => ({
              message: e.message,
              file: e.fileName,
              line: e.lineNumber,
              column: e.columnNumber,
            })),
          },
          error: uncaughtErrors.length > 0
            ? `${uncaughtErrors.length} uncaught errors`
            : undefined,
        };
      },
    },
    {
      id: 'promise-rejections',
      label: 'Unhandled promise rejections',
      category: 'Promises',
      description: 'Detecting unhandled async errors',
      check: async () => {
        const rejections = errors.filter(e => e.type === 'unhandledRejection');

        return {
          passed: rejections.length === 0,
          details: {
            count: rejections.length,
            rejections: rejections.slice(0, 3).map(e => e.message),
          },
          error: rejections.length > 0
            ? `${rejections.length} unhandled promise rejections`
            : undefined,
        };
      },
    },
    {
      id: 'error-patterns',
      label: 'Error pattern analysis',
      category: 'Patterns',
      description: 'Identifying recurring error patterns',
      check: async () => {
        const recurringPatterns = errorPatterns.filter(p => p.count > 1);

        return {
          passed: recurringPatterns.length === 0,
          details: {
            uniquePatterns: errorPatterns.length,
            recurringPatterns: recurringPatterns.length,
            topPatterns: recurringPatterns
              .sort((a, b) => b.count - a.count)
              .slice(0, 5)
              .map(p => ({
                pattern: p.pattern,
                count: p.count,
                type: p.type,
              })),
          },
          error: recurringPatterns.length > 0
            ? `${recurringPatterns.length} recurring error patterns`
            : undefined,
        };
      },
    },
    {
      id: 'error-frequency',
      label: 'Error frequency analysis',
      category: 'Metrics',
      description: 'Measuring error rates',
      check: async () => {
        const now = Date.now();
        const recentErrors = errors.filter(e =>
          now - new Date(e.timestamp).getTime() < 60000 // Last minute
        );

        const errorRate = recentErrors.length;
        const acceptable = errorRate < 5;

        return {
          passed: acceptable,
          details: {
            errorsPerMinute: errorRate,
            totalErrors: errors.length,
            oldestError: errors[errors.length - 1]?.timestamp,
            newestError: errors[0]?.timestamp,
          },
          error: !acceptable
            ? `High error rate: ${errorRate} errors/minute`
            : undefined,
        };
      },
    },
    {
      id: 'browser-compatibility',
      label: 'Browser compatibility check',
      category: 'Environment',
      description: 'Checking for browser-specific errors',
      check: async () => {
        const userAgent = navigator.userAgent;
        const isChrome = userAgent.includes('Chrome');
        const isFirefox = userAgent.includes('Firefox');
        const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');

        const browserSpecificErrors = errors.filter(e =>
          (e.message.includes('webkit') && !isSafari) ||
          (e.message.includes('moz') && !isFirefox) ||
          (e.message.includes('chrome') && !isChrome)
        );

        return {
          passed: browserSpecificErrors.length === 0,
          details: {
            browser: isChrome ? 'Chrome' : isFirefox ? 'Firefox' : isSafari ? 'Safari' : 'Other',
            userAgent,
            browserSpecificErrors: browserSpecificErrors.length,
          },
        };
      },
    },
    {
      id: 'source-maps',
      label: 'Source map availability',
      category: 'Debugging',
      description: 'Checking if source maps are working',
      check: async () => {
        const hasSourceInfo = errors.some(e =>
          e.fileName && !e.fileName.includes('.js:1:') && e.lineNumber && e.lineNumber > 1
        );

        return {
          passed: hasSourceInfo || errors.length === 0,
          details: {
            errorsWithSourceInfo: errors.filter(e => e.fileName && e.lineNumber).length,
            errorsWithoutSourceInfo: errors.filter(e => !e.fileName || !e.lineNumber).length,
          },
          error: !hasSourceInfo && errors.length > 0
            ? 'Source maps may not be working correctly'
            : undefined,
        };
      },
    },
  ];

  const clearErrors = () => {
    setErrors([]);
    setErrorPatterns([]);
  };

  const triggerTestError = () => {
    console.error('Test error from ErrorScience component');
    setTimeout(() => {
      throw new Error('Test uncaught error');
    }, 100);
    Promise.reject('Test promise rejection');
  };

  // Reproducible test cases for common error scenarios
  const testCases = [
    {
      id: 'network-timeout',
      name: 'Network Timeout',
      description: 'Simulate a network request timeout',
      trigger: async () => {
        const controller = new AbortController();
        setTimeout(() => controller.abort(), 100);
        try {
          await fetch('https://jsonplaceholder.typicode.com/posts/1', {
            signal: controller.signal,
          });
        } catch (e) {
          throw new Error(`Network timeout: ${e}`);
        }
      },
    },
    {
      id: 'null-reference',
      name: 'Null Reference',
      description: 'Simulate accessing property of null',
      trigger: () => {
        const obj: any = null;
        return obj.property.subProperty;
      },
    },
    {
      id: 'array-bounds',
      name: 'Array Out of Bounds',
      description: 'Simulate array index error',
      trigger: () => {
        const arr = [1, 2, 3];
        return arr[10].toString();
      },
    },
    {
      id: 'async-rejection',
      name: 'Async Rejection',
      description: 'Simulate unhandled promise rejection',
      trigger: async () => {
        return Promise.reject(new Error('Simulated async rejection'));
      },
    },
    {
      id: 'type-error',
      name: 'Type Error',
      description: 'Simulate type coercion error',
      trigger: () => {
        const num: any = '123';
        return num.toFixed(2);
      },
    },
    {
      id: 'dom-not-found',
      name: 'DOM Element Not Found',
      description: 'Simulate missing DOM element',
      trigger: () => {
        const element = document.querySelector('#non-existent-element');
        element!.classList.add('test');
      },
    },
  ];

  const runTestCase = async (testCase: typeof testCases[0]) => {
    try {
      await testCase.trigger();
    } catch (error) {
      console.error(`Test case '${testCase.name}' triggered:`, error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-red-100 to-orange-100 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          🔬 Error Science Laboratory
        </h2>
        <p className="text-gray-700 mb-4">
          Real-time error monitoring and diagnosis
        </p>

        <div className="flex gap-4 mb-4">
          <button
            onClick={isMonitoring ? stopMonitoring : startMonitoring}
            className={`px-4 py-2 rounded-lg font-medium ${
              isMonitoring
                ? 'bg-green-600 text-white'
                : 'bg-gray-600 text-white'
            }`}
          >
            {isMonitoring ? '📡 Monitoring Active' : '🔌 Start Monitoring'}
          </button>
          <button
            onClick={clearErrors}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium"
          >
            🗑️ Clear Errors
          </button>
          <button
            onClick={triggerTestError}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg font-medium"
          >
            💥 Trigger Test Errors
          </button>
        </div>

        {/* Error Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white p-4 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{errors.length}</div>
            <div className="text-sm text-gray-600">Total Errors</div>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {new Set(errors.map(e => e.message)).size}
            </div>
            <div className="text-sm text-gray-600">Unique Errors</div>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {errorPatterns.length}
            </div>
            <div className="text-sm text-gray-600">Error Patterns</div>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {errors.filter(e => e.count > 1).length}
            </div>
            <div className="text-sm text-gray-600">Recurring Errors</div>
          </div>
        </div>

        {/* Recent Errors */}
        {errors.length > 0 && (
          <div className="bg-white rounded-lg p-4 max-h-60 overflow-y-auto">
            <h3 className="font-semibold text-gray-900 mb-2">Recent Errors:</h3>
            <div className="space-y-2">
              {errors.slice(0, 5).map(error => (
                <div key={error.id} className="border-l-4 border-red-500 pl-3 py-2">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium text-red-700">
                        {error.message}
                      </div>
                      {error.fileName && (
                        <div className="text-xs text-gray-600">
                          {error.fileName}:{error.lineNumber}:{error.columnNumber}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {error.count > 1 && (
                        <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full mr-2">
                          ×{error.count}
                        </span>
                      )}
                      <span className="bg-gray-100 px-2 py-1 rounded">
                        {error.type}
                      </span>
                    </div>
                  </div>
                  {error.stack && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-600 cursor-pointer">
                        Show stack trace
                      </summary>
                      <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-x-auto">
                        {error.stack}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <ScienceDebugger
        title="🔍 Error Diagnostics"
        description="Analyzing captured errors for patterns and issues"
        steps={errorDiagnosticSteps}
        groupByCategory={true}
        animationDelay={200}
        autoRun={false}
      />

      {/* Error Pattern Analysis */}
      {errorPatterns.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            📊 Error Pattern Analysis
          </h3>
          <div className="space-y-3">
            {errorPatterns
              .sort((a, b) => b.count - a.count)
              .slice(0, 10)
              .map((pattern, index) => (
                <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {pattern.pattern.substring(0, 100)}...
                    </div>
                    <div className="text-xs text-gray-600">
                      Type: {pattern.type} | First: {new Date(pattern.firstSeen).toLocaleTimeString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-red-600">{pattern.count}</div>
                    <div className="text-xs text-gray-600">occurrences</div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Reproducible Test Cases */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          🧪 Reproducible Test Cases
        </h3>
        <p className="text-gray-600 mb-4">
          Click any test case to trigger a specific error scenario and observe how the system handles it
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testCases.map((testCase) => (
            <button
              key={testCase.id}
              onClick={() => runTestCase(testCase)}
              className="p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200 hover:border-orange-400 hover:shadow-md transition-all text-left"
            >
              <h4 className="font-semibold text-orange-900 mb-1">{testCase.name}</h4>
              <p className="text-sm text-gray-700">{testCase.description}</p>
              <p className="text-xs text-orange-600 mt-2 font-medium">Click to trigger →</p>
            </button>
          ))}
        </div>
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            💡 <strong>Tip:</strong> After triggering test cases, check the Error Log above to see how errors are captured.
            Use the diagnostics to analyze error patterns and frequencies.
          </p>
        </div>
      </div>
    </div>
  );
};